import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import Loader from "../../components/Loader";
import { useQuery } from "@tanstack/react-query";
import ChartIncidents from "./ChartIncidents";
import IncidentsFilterChart from "./IncidentsFilterChart";
import { Link } from "react-router-dom";
import TableIncidents from "./TableIncidents";
import NumberCard from "../../components/Card/NumberCard";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

const monthNamesMap = {
  "Janvier": "01",
  "Février": "02",
  "Mars": "03",
  "Avril": "04",
  "Mai": "05",
  "Juin": "06",
  "Juillet": "07",
  "Août": "08",
  "Septembre": "09",
  "Octobre": "10",
  "Novembre": "11",
  "Décembre": "12"
};

export default function Incidents() {
  const { user, apiUrl } = useContext(AuthContext);
  const [incidentsFilteredByGraph, setIncidentsFilteredByGraph] = useState([]);
  const [isShowAllTickets, setisShowAllTickets] = useState(false);
  const [isChartClicked, setisChartClicked] = useState(false);
  const [currentSlide, setCurrentSlide] = useState("0");
  const [timeFilter, setTimeFilter] = useState("twelve");
  const [quarterSelected, setQuarterSelected] = useState(1);
  const [incidentsFilteredByTime, setIncidentsFilteredByTime] = useState([]);
  const [ticketNumber, setTicketNumber] = useState(0);
  const [siteChoices, setSiteChoices] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [filter, setFilter] = useState(null);
  const [label, setLabel] = useState(null);
  const monthNames = Object.keys(monthNamesMap);
  const currentMonth = new Date().getMonth() + 1;
  const formattedMonth = currentMonth.toString().padStart(2, '0');
  const [monthsFilter, setMonthsFilter] = useState(formattedMonth);
  const { t } = useTranslation();

  const originalLabels = {
    "Issue": "Probleme",
    "Minor change": "Changement mineur",
    "Major change": "Changement majeur",
    "Incident outside of contract": "Incident hors-contrat",
    "Alerts": "Alerte",
    "High S2": "Haute S2",
    "Medium S3": "Moyenne S3",
    "Low S4": "Basse S4",
    "Critical S1": "Critique S1",
    "SLA respected": "SLA RESPECTE",
    "SLA exceeded": "SLA DEPASSE",
  };

  const valuesType = [
    "Nombre de tickets",
    "Sévérité",
    "SLA",
    "Temps de résolution",
    "Sujet des incidents par contact",
    "Sujet des demandes par contact",
    "Sujet des incidents par date",
    "Sujet des demandes par date",
    "Changements ouverts par mois",
    "Changements clos par mois",
    "Incidents ouverts par mois",
    "Incidents clos par mois",
    "Nombre de tickets par sites",
    "Tickets sla par sites",
    "Types de tickets par sites",
    ...(user.entrepriseId === 32 ? ["Temps moyen par utilisateurs"] : [])
  ];

  useEffect(() => {
    setSelectedSite("");
    setSelectedUser("");
    setFilter(null);
    setLabel(null);
    setisChartClicked(false);
    setIncidentsFilteredByGraph([]);
    if (currentSlide === "15") {
      setTimeFilter("twelve");
    }
  }, [currentSlide]);

  const getIncidentsGraph = async () => {
    try {
      const res = await axios(`${apiUrl}incident`, {
        method: "GET",
        withCredentials: true,
      });

      const data = res.data;

      const sortedAndFilteredData = {};
      let totalTickets = 0;
      const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
      const getSortableDate = (item) => {
        const rawDate = item?.createdon || item?.resolveby || item?.date || item?.month;

        if (!rawDate) {
          return null;
        }

        const parsedDate = new Date(rawDate);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
      };
      const normalizeArray = (value) => {
        if (value.length === 0) {
          return [];
        }

        const containsObjectsOnly = value.every((item) => isObject(item));

        if (!containsObjectsOnly) {
          return [...value];
        }

        const shouldFilterByActive = value.some((item) => Object.prototype.hasOwnProperty.call(item, "is_active"));
        const shouldSortByDate = value.some((item) => getSortableDate(item));
        let normalizedData = [...value];

        if (!user.roles.includes("ROLE_SUPERADMIN") && shouldFilterByActive) {
          normalizedData = normalizedData.filter((item) => item.is_active === "1");
        }

        if (shouldSortByDate) {
          normalizedData = normalizedData.sort((a, b) => {
            const dateA = getSortableDate(a);
            const dateB = getSortableDate(b);

            if (!dateA && !dateB) {
              return 0;
            }

            if (!dateA) {
              return 1;
            }

            if (!dateB) {
              return -1;
            }

            return dateB - dateA;
          });
        }

        return normalizedData;
      };
      const normalizeValue = (value) => {
        if (Array.isArray(value)) {
          return normalizeArray(value);
        }

        if (isObject(value)) {
          return Object.keys(value).reduce((acc, nestedKey) => {
            acc[nestedKey] = normalizeValue(value[nestedKey]);
            return acc;
          }, {});
        }

        return value;
      };
      const countTicketsInValue = (value) => {
        if (Array.isArray(value)) {
          if (!value.every((item) => isObject(item))) {
            return 0;
          }

          return value.filter((item) =>
            Object.prototype.hasOwnProperty.call(item, "ticketnumber") ||
            Object.prototype.hasOwnProperty.call(item, "createdon") ||
            Object.prototype.hasOwnProperty.call(item, "incidentid")
          ).length;
        }

        if (isObject(value)) {
          return Object.values(value).reduce((acc, nestedValue) => acc + countTicketsInValue(nestedValue), 0);
        }

        return 0;
      };

      if (user.roles.length > 0) {
        Object.keys(data).forEach((key) => {
          const normalizedValue = normalizeValue(data[key]);
          sortedAndFilteredData[key] = normalizedValue;
          totalTickets += countTicketsInValue(normalizedValue);
        });
      }

      sortedAndFilteredData.ticketsnumber = totalTickets;
      return sortedAndFilteredData;

    } catch (error) {
      console.error("Error fetching incidents:", error);
      return {}; // Retourner un objet vide en cas d'erreur
    }
  };

  const incidents = useQuery({
    queryKey: ["incidents"],
    queryFn: getIncidentsGraph,
  });

  const getIncidentsList = async () => {
    try {
      const res = await axios(`${apiUrl}incident/index`, {
        method: "GET",
        withCredentials: true,
      });

      if (user.roles.length > 0) {
        if (user.roles.includes("ROLE_SUPERADMIN")) {
          return res.data.sort((a, b) => new Date(b.createdon) - new Date(a.createdon))
        } else {
          return res.data.filter((incident) => incident.is_active === "1").sort((a, b) => new Date(b.createdon) - new Date(a.createdon));
        }
      }
    } catch (error) {
      console.error("Error fetching incidents:", error);
      return {};
    }
  };

  const incidentsList = useQuery({
    queryKey: ["incidentsList"],
    queryFn: getIncidentsList,
  });

  const handleIncidentsFilteredByTime = () => {
    const todayDate = new Date();
    const currentMonth = todayDate.getMonth();
    const currentYear = todayDate.getFullYear();

    const getQuarterDates = (year, quarter) => {
      const startMonth = (quarter - 1) * 3;
      const startDate = new Date(year, startMonth, 1);
      const endDate = new Date(year, startMonth + 3, 0);
      return { startDate, endDate };
    };

    const filterDates = {
      year: new Date(currentYear, 0, 1),
      twelve: new Date(currentYear, currentMonth - 13, 1),
      months: new Date(currentYear, currentMonth, 1),
      week: new Date(currentYear, currentMonth, todayDate.getDate() - 7),
      quarter: getQuarterDates(currentYear, quarterSelected).startDate
    };

    const monthPadded = monthsFilter.padStart(2, '0');
    const currentYearTag = `${currentYear}-${monthPadded}`;
    const lastYearTag = `${currentYear - 1}-${monthPadded}`;

    const hasCurrentYearData = Object.values(incidents.data).some(array =>
      Array.isArray(array) && array.some(item =>
        item.month === currentYearTag ||
        item.date === currentYearTag ||
        item.createdon?.includes(currentYearTag)
      )
    );

    const targetMonthFilter = hasCurrentYearData ? currentYearTag : lastYearTag;

    const filterByDate = (item, key, applySiteFilter = true) => {
      const match = !applySiteFilter || !selectedSite || item.name === selectedSite;
      if (timeFilter === "all") {
        return match;
      }

      const itemDate = new Date(item.month || item.createdon || item.date);

      if (timeFilter === "twelve" && itemDate >= filterDates.twelve) {
        return match;
      } else if (timeFilter === "months" && monthsFilter !== null) {
        return match && (
          item.month === targetMonthFilter ||
          item.date === targetMonthFilter ||
          item.resolveby === targetMonthFilter ||
          item.createdon === targetMonthFilter
        );
      } else if (timeFilter === "quarter") {
        const { startDate, endDate } = getQuarterDates(currentYear, quarterSelected);
        const normalizedStartDate = startDate.setHours(0, 0, 0, 0);
        const normalizedEndDate = endDate.setHours(23, 59, 59, 999);
        const normalizedItemDate = itemDate.setHours(0, 0, 0, 0);
        return match && normalizedItemDate >= normalizedStartDate && normalizedItemDate <= normalizedEndDate;
      }
      return false;
    };

    const sortData = (a, b, key) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      if (key === "graph_sla_site") {
        return (a.sla || "").localeCompare(b.sla || "");
      }
      return (a.casetypecode || "").localeCompare(b.casetypecode || "");
    };

    const incidentsFilteredByTime = Object.keys(incidents.data).reduce((acc, key) => {
      const value = incidents.data[key];
      if (key === "graph_ticket_category_month" && value && typeof value === "object" && !Array.isArray(value)) {
        acc[key] = value;
        return acc;
      }
      if (!Array.isArray(value)) return acc;
      if (key === "graph_sla_site" || key === "graph_ticket_site") {
        const filtered = value.filter(item => filterByDate(item, key, true));
        const finalSorted = filtered.sort((a, b) => sortData(a, b, key));
        acc[key] = finalSorted;
      } else {
        const filteredData = value.filter((item) => {
          if (key === "graph_camembert_date" || key === "graph_ho") {
            return filterByDate(item, key, false);
          } else {
            const filterDate = new Date(item.resolveby || item.createdon);
            const isActive = item.is_active === "1";
            if (timeFilter === "all") {
              return isActive;
            } else if (timeFilter === "twelve" && filterDate >= filterDates.twelve) {
              return isActive;
            } else if (timeFilter === "months") {
              return isActive && (
                (item.month && item.month.includes(targetMonthFilter)) ||
                (item.date && item.date.includes(targetMonthFilter)) ||
                (item.resolveby && item.resolveby.includes(targetMonthFilter)) ||
                (item.createdon && item.createdon.includes(targetMonthFilter))
              );
            } else if (timeFilter === "quarter") {
              const { startDate, endDate } = getQuarterDates(currentYear, quarterSelected);
              const normalizedStartDate = startDate.setHours(0, 0, 0, 0);
              const normalizedEndDate = endDate.setHours(23, 59, 59, 999);
              const normalizedItemDate = filterDate.setHours(0, 0, 0, 0);
              return isActive && normalizedItemDate >= normalizedStartDate && normalizedItemDate <= normalizedEndDate;
            }
            return false;
          }
        });
        acc[key] = filteredData;
      }
      return acc;
    }, {});
    setIncidentsFilteredByTime(incidentsFilteredByTime);
  };

  const extractSitesFromIncidents = (data) => {
    const uniqueNames = new Set();

    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (!Array.isArray(value)) return;

      value.forEach((item) => {
        if (item.name) {
          uniqueNames.add(item.name);
        }
      });
    });

    const namesArray = Array.from(uniqueNames);
    setSiteChoices(namesArray);
    if (namesArray.length > 0 && !selectedSite) {
      setSelectedSite(namesArray[0]);
    }
  };

  useEffect(() => {
    if (!incidents.isLoading && incidents.data) {
      extractSitesFromIncidents(incidents.data);
      handleIncidentsFilteredByTime();
    }
  }, [incidents.data, timeFilter, monthsFilter, selectedSite, quarterSelected]);

  // Set new data when user's click
  const handleGraphClick = (event, chartRef) => {
    const chartInstance = chartRef;
    const activePoints = chartInstance.getElementsAtEventForMode(
      event,
      "nearest",
      { intersect: true },
      true
    );
    if (activePoints.length > 0) {
      let label;
      const firstPoint = activePoints[0];
      const untranslatedLabel = chartInstance.data.datasets[firstPoint.datasetIndex].label;
      const filter = chartInstance.data.labels[firstPoint.index];
      label = originalLabels[untranslatedLabel] || untranslatedLabel;
      setFilter(filter);
      setLabel(label);
    }
  };

  const handleIncidentsClickOnGraph = async () => {
    if (label !== null && filter !== null) {
      try {
        let filterFn;
        let statuscode;
        let selectedSiteId = null;
        let apiDateParam = "createdon";
        let apiFilterValue = filter;

        const today = new Date();
        const currentYear = today.getFullYear();

        const getQuarterDates = (year, quarter) => {
          const startMonth = (quarter - 1) * 3;
          const startDate = new Date(year, startMonth, 1);
          const endDate = new Date(year, startMonth + 3, 0);
          return { startDate, endDate };
        };

        if (timeFilter === "months") {
          const monthPadded = monthsFilter.padStart(2, '0');
          const currentYearTag = `${currentYear}-${monthPadded}`;
          const lastYearTag = `${currentYear - 1}-${monthPadded}`;

          const hasCurrentYearData = Object.values(incidents.data).some(array =>
            Array.isArray(array) && array.some(item =>
              item.month === currentYearTag || item.date === currentYearTag || item.createdon?.includes(currentYearTag)
            )
          );
          apiFilterValue = hasCurrentYearData ? currentYearTag : lastYearTag;
        } else {
          apiFilterValue = filter;
        }

        switch (currentSlide) {
          case "0":
            statuscode = "casetypecode";
            filterFn = (incident) =>
              incident.casetypecode === label &&
              incident.createdon.includes(filter) &&
              incident.statuscode === "5";
            break;
          case "1":
            statuscode = "prioritycode"
            filterFn = (incident) =>
              incident.prioritycode === label &&
              incident.createdon.includes(filter) &&
              incident.casetypecode === "Incident";
            break;
          case "2":
            statuscode = "sla";
            apiDateParam = "resolveby";
            filterFn = (incident) =>
              incident.sla === label &&
              incident.resolveby.includes(filter) &&
              incident.casetypecode === "Incident" &&
              incident.subject !== "Cybersécurité";
            break;
          // case "12":
          //   statuscode = "site";
          //   apiDateParam = "createdon";

          //   let clickedSiteId = null;
          //   let clickedSiteDate = null; // On ajoute une variable pour la date du site

          //   if (incidents.data?.graph_camembert_date) {
          //     // On cherche l'objet complet qui correspond au site cliqué
          //     const found = incidents.data.graph_camembert_date.find(item => item.name === filter);

          //     if (found) {
          //       clickedSiteId = found.id;
          //       clickedSiteDate = found.date; // On récupère la date (ex: "2024-01")
          //     }
          //   }

          //   // --- CORRECTION CRUCIALE ---
          //   // Si on est dans le mode "months" (Camembert par date), 
          //   // on force la valeur de l'API avec la date de l'objet cliqué
          //   if (timeFilter === "months" && clickedSiteDate) {
          //     apiFilterValue = clickedSiteDate;
          //   }

          //   filterFn = (incident) => {
          //     const incidentDate = new Date(incident.createdon);
          //     const isActive = incident.is_active === "1";
          //     const matchesSite = String(incident.site_id) === String(clickedSiteId);

          //     if (timeFilter === "months") {
          //       // On filtre localement avec la date récupérée
          //       return isActive && matchesSite && incident.createdon.includes(apiFilterValue);
          //     } else if (timeFilter === "quarter") {
          //       const { startDate, endDate } = getQuarterDates(currentYear, quarterSelected);
          //       return isActive && matchesSite && incidentDate >= startDate && incidentDate <= endDate;
          //     } else if (timeFilter === "twelve") {
          //       const twelveMonthsAgo = new Date();
          //       twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 13);
          //       return isActive && matchesSite && incidentDate >= twelveMonthsAgo;
          //     }

          //     return isActive && matchesSite;
          //   };
          //   break;
          case "13":
            statuscode = "sla";
            apiDateParam = "createdon";

            if (selectedSite && incidents.data) {
              Object.values(incidents.data).forEach(arr => {
                if (Array.isArray(arr) && !selectedSiteId) {
                  const found = arr.find(item => item.name === selectedSite);
                  if (found) selectedSiteId = found.id;
                }
              });
            }

            filterFn = (incident) => {
              const incidentDate = new Date(incident.createdon);
              const isActive = incident.is_active === "1";
              const matchesSite = !selectedSiteId || incident.site_id === selectedSiteId;
              const matchesLabel = incident.sla === label && incident.casetypecode === "Incident";

              if (timeFilter === "months") {
                return isActive && matchesSite && matchesLabel && incident.createdon.includes(apiFilterValue);
              } else if (timeFilter === "quarter") {
                const { startDate, endDate } = getQuarterDates(currentYear, quarterSelected);
                return isActive && matchesSite && matchesLabel && incidentDate >= startDate && incidentDate <= endDate;
              }
              return isActive && matchesSite && matchesLabel && incident.createdon.includes(filter);
            };
            break;
          case "14":
            statuscode = "casetypecode";
            apiDateParam = "createdon";

            if (selectedSite && incidents.data) {
              Object.values(incidents.data).forEach(arr => {
                if (Array.isArray(arr) && !selectedSiteId) {
                  const found = arr.find(item => item.name === selectedSite);
                  if (found) selectedSiteId = found.id;
                }
              });
            }

            filterFn = (incident) => {
              const incidentDate = new Date(incident.createdon);
              const isActive = incident.is_active === "1";
              const matchesSite = !selectedSiteId || incident.site_id === selectedSiteId;
              const matchesLabel = incident.casetypecode === label && incident.statuscode === "5";

              if (timeFilter === "months") {
                return isActive && matchesSite && matchesLabel && incident.createdon.includes(apiFilterValue);
              } else if (timeFilter === "quarter") {
                const { startDate, endDate } = getQuarterDates(currentYear, quarterSelected);
                return isActive && matchesSite && matchesLabel && incidentDate >= startDate && incidentDate <= endDate;
              }
              return isActive && matchesSite && matchesLabel && incident.createdon.includes(filter);
            };
            break;
          default:
            return;
        }

        const labelForApi = currentSlide === "12" ? filter : label;

        const res = await axios.get(
          `${apiUrl}incident/index?${statuscode}=${labelForApi}&${apiDateParam}=${apiFilterValue}`,
          { withCredentials: true }
        );

        const filteredIncidents = res.data.filter(filterFn);
        setIncidentsFilteredByGraph(filteredIncidents);

        if (!isShowAllTickets) {
          setisShowAllTickets(true);
          setisChartClicked(true);
        }
      } catch (error) {
        console.error("Erreur axios / filtre :", error);
      }
    }
  };

  useEffect(() => {
    if (filter !== null && label !== null) {
      handleIncidentsClickOnGraph();
    }
  }, [filter, label, incidents.data]);

  const hasNoData = (data) => {
    return Object.entries(data).every(([key, value]) => {
      if (key === "ticketsnumber") {
        return true;
      }
      if (Array.isArray(value)) {
        return value.every((item) => item === "No data");
      }
      return false;
    });
  };

  if (incidents.data && hasNoData(incidents.data)) {
    return (
      <div className="ecp-container">
        <div className="ecp-container-content-error">
          <div className="error-container">
            <img src='../assets/ecp_error.svg' alt="icône d'erreur" />
            <div className="error-container-text">
              <h1 className="page-title">
                {t("Aucun ticket disponible")}
              </h1>
              <p className="page-link_home">
                <Link to="/"> {t("Revenir à l'accueil")}</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDisplayAllTickets = () => {
    setisShowAllTickets(!isShowAllTickets);
    setisChartClicked(false);
    setIncidentsFilteredByGraph([]);
    setLabel(null);
    setFilter(null);
  };

  return (
    <div className="ecp-container">
      <div className="ecp-container-content">
        {!incidents.isLoading ? (
          <>
            <div className="ecp-container-content-header">
              <div className="ecp-container-content-header-title">
                <div className="page-title page-logo">
                  <h1 className="page-title">Tickets</h1>
                </div>
              </div>
              {!isShowAllTickets ? (
                <button
                  className="button-incidents-all buttons-ecp button-ecp-new"
                  onClick={
                    () => {
                      handleDisplayAllTickets();
                    }
                  }
                >
                  {t('Afficher tous les tickets')}
                </button>
              ) : (
                <button
                  className="buttons-ecp buttons-ecp-new"
                  onClick={() => {
                    handleDisplayAllTickets();
                  }}
                >
                  {t('Retour au graphique')}
                </button>
              )}
            </div>
            {isShowAllTickets ? (
              <>
                <TableIncidents
                  incidents={incidentsList}
                  handleDisplayAllTickets={handleDisplayAllTickets}
                  incidentsFilteredByGraph={incidentsFilteredByGraph}
                  isChartClicked={isChartClicked}
                />
              </>
            ) : (
              <>
                <section className="page-section">
                  <NumberCard
                    title="Tickets"
                    number={!incidents.isLoading ? ticketNumber : null}
                    logo={faCircleCheck}
                    color={"#AEC353"}
                    stylesIcons={"logo-certificats"}
                  />
                </section>
                <section className="page-section container_ticket_per_site">
                  <IncidentsFilterChart
                    setCurrentSlide={setCurrentSlide}
                    setTimeFilter={setTimeFilter}
                    timeFilter={timeFilter}
                    currentSlide={currentSlide}
                    valuesType={valuesType}
                    siteChoices={siteChoices}
                    setSelectedSite={setSelectedSite}
                    selectedSite={selectedSite}
                    quarterSelected={quarterSelected}
                    setQuarterSelected={setQuarterSelected}
                    selectedUser={selectedUser}
                    setSelectedUser={setSelectedUser}
                  />
                  {timeFilter === "months" && (
                    <div className="page-section_form form_months_sites">
                      <form className="form">
                        <div className="form-input_container">
                          <label htmlFor="type">{t('Sélectionnez le mois')}</label>
                          <select
                            className="only only_incidents"
                            id="type"
                            onChange={(e) => {
                              setMonthsFilter(e.target.value);
                            }}
                            value={monthsFilter}
                          >
                            {monthNames.map((month) => (
                              <option key={monthNamesMap[month]} value={monthNamesMap[month]}>
                                {t(month)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </form>
                    </div>
                  )}
                </section>
                <ChartIncidents
                  incidents={incidentsFilteredByTime}
                  currentSlide={currentSlide}
                  handleGraphClick={handleGraphClick}
                  timeFilter={timeFilter}
                  setTicketNumber={setTicketNumber}
                  selectedUser={selectedUser}
                />
              </>
            )}
          </>
        ) : (
          <Loader />
        )}
      </div>
    </div >
  );
}
